import { createContext } from "preact";
import { createRxDatabase, defaultHashSha256, randomToken, RxCollection } from "rxdb";
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';

import { SimplePeer } from "rxdb/plugins/replication-webrtc";
import { SolidAuthenticationClient } from "../util/solid-auth.js";
import { localConfigStorage } from "../util/local-config.js";

import * as chats from "./chatMessages.js";
import * as items from "./items.js";

const webrtcReplicationImport = import('rxdb/plugins/replication-webrtc');
const solidReplicationImport = import('@jeswr/rxdb-solid');

// @ts-ignore
window.process = { nextTick: (fn, ...args) => setTimeout(() => fn(...args)) };

// All the types of U (expecting a union - one set of properties expected) are "any"
// and a function taking them is a function taking I (all properties allowed/expected)
type UnionToIntersection<U> = 
  (U extends any ? (k: U) => void : never) extends ((k: infer I) => void) ? I : never;

function mergeArray<T extends object[]>(arr: T): UnionToIntersection<T[number]> {
  return Object.assign({}, ...arr);
}

const collections = {
  // [items.collectionName]: items,
  [chats.collectionName]: chats,
};

// Prepare for WebRTC and Solid
export const authClient = new SolidAuthenticationClient();
const authenticated = await authClient.doFlowsIfNecessary();
const roomId = window.location.hash;

if (!roomId || roomId.length < 5) {
    window.location.hash = 'room-' + randomToken(10);
    window.location.reload();
}
const roomHash = await defaultHashSha256(roomId);
localConfigStorage.getValue("recentRooms").then(x => {
  if (!x) {
    localConfigStorage.storeValue("recentRooms", { [roomId]: {} });
  } else if (!x[roomId]) {
    x[roomId] = {};
    localConfigStorage.storeValue("recentRooms", x);
  }
});

export const createDb = async (solidContainerBase: string = 'https://george.datapod.igrant.io/rxdbtest/') => {
  console.log("Create DB");
  const bareDb = await createRxDatabase({
    name: roomHash,
    storage: getRxStorageDexie(),
    cleanupPolicy: {
      minimumDeletedTime: 1000 * 60 * 60 * 24,
      runEach: 1000 * 60 * 60 * 24,
      awaitReplicationsInSync: true,
    },
  })
  const db = await bareDb.addCollections(mergeArray(Object.values(collections).map(c => c.collectionCreators)));

  Object.values(collections).map(x => x.postHandler(db));

  async function setUpReplication<T>(collection: RxCollection<T>, container: string, collectionName: string) {
    const { replicateSolid } = await solidReplicationImport;

    // Solid replication
    console.log(`Setting up Solid replication for ${collectionName}`);
    const solidReplication = await replicateSolid({
      collection,
      container,
      fetch: authClient.authenticatedFetch.bind(authClient), // an authenticated fetch (e.g. from @solid/reactive-authentication)
    });
    authClient.onLoginChanged(authenticated => {
      if (authenticated) {
        solidReplication.start();
      } else {
        solidReplication.pause();
      }
    })
    if (authenticated) await solidReplication.awaitInitialReplication(); // the collection is now synced with the pod

    // WebRTC replication
    console.log(`Setting up webrtc replication for ${collectionName}`);
    const { getConnectionHandlerSimplePeer, replicateWebRTC } = await webrtcReplicationImport;
    const rtcReplication = await replicateWebRTC<any, SimplePeer>({
        collection,
        connectionHandlerCreator: getConnectionHandlerSimplePeer({}),
        topic: roomHash.substring(0, 10) + "-" + collectionName,
        pull: {},
        push: {},
    });
    rtcReplication.error$.subscribe((err: any) => {
        console.log('replication error:');
        console.dir(err);
    });
    rtcReplication.peerStates$.subscribe(s => {
        console.log('new peer states:');
        console.dir(s);
    });
    await rtcReplication.awaitFirstPeer();
    console.log(`Done with replication setup: ${collectionName}`);
  }

  console.log("Set up replication");
  (Object.keys(collections) as Array<keyof typeof collections>).map(collectionName => {
    setUpReplication(db[collectionName], `${solidContainerBase}${collectionName}/${roomHash}`, collectionName);
  });

  console.log("Return DB");

  return db;
}

export type DbType = Awaited<ReturnType<typeof createDb>>;
export const DatabaseProvider = createContext(null as (null | DbType));

